import { useEffect, useRef } from 'react';
import type { Arealplan } from '@origo/arealplan';
import { useArealplanClient } from '@origo/arealplan';
import { isMeasureToolOpen, selectDisableMapClick, setDisableMapClick } from '@origo/map-context';
import { createLogger } from '@origo/logger';
import { useAppDispatch, useAppSelector } from '../../../store/storeHooks';

const log = createLogger('usePlanMapPick');

export interface PlanMapPick {
    /** Planene hvis planområde inneholder klikkpunktet. Kan være tom. */
    plans: Arealplan[];
    /** Klikkpunktet i kartets SR — brukes til å plassere velger-popupen. */
    mapPoint: __esri.Point;
}

export interface UsePlanMapPickProps {
    /** Live MapView/SceneView fra useMapViewContext(). null til kartet er klart. */
    mapView: __esri.MapView | __esri.SceneView | null | undefined;
    /** Hovedbryter — klikk-handleren kobles kun på når dette er sant. */
    enabled: boolean;
    /** Kalles når spørringen for et klikk er ferdig. */
    onPick: (pick: PlanMapPick) => void;
}

/**
 * Finn plan via kartet: på plansøk-landingssiden spør et kartklikk plan-
 * tjenestene etter planer som inneholder punktet (`findByPoint`), uavhengig av
 * hvilke kartlag som er synlige. Treffene har samme form som plan-lista, så
 * kalleren kan slå dem opp med `planKey`.
 *
 * Et nytt klikk avbryter spørringen fra forrige klikk, så bare siste klikk
 * gir svar. Spørringen avbrytes også når brukeren går ut av kartet (klikker
 * eller tabber til lista) før svaret kommer, så velgeren ikke dukker opp og
 * tar fokus. Klikk ignoreres mens måleverktøyet er åpent.
 *
 * Appens standard kart-klikk (lokasjonsoppslag) dempes mens hooken er aktiv,
 * på samme måte som `useDelomradeContextMapPick`.
 */
export function usePlanMapPick({ mapView, enabled, onPick }: UsePlanMapPickProps): void {
    const dispatch = useAppDispatch();
    const arealplanClient = useArealplanClient();
    const disableMapClick = useAppSelector(selectDisableMapClick);

    const onPickRef = useRef(onPick);
    useEffect(() => { onPickRef.current = onPick; }, [onPick]);

    useEffect(() => {
        if (!enabled || !mapView) return;

        dispatch(setDisableMapClick(true));
        let controller: AbortController | null = null;

        const handle = mapView.on('click', (event: __esri.ViewClickEvent) => {
            const point = event.mapPoint;
            if (!point || isMeasureToolOpen(mapView)) return;

            controller?.abort();
            const current = new AbortController();
            controller = current;

            arealplanClient.arealplaner
                .findByPoint(point, { signal: current.signal })
                .then((plans) => {
                    if (current.signal.aborted) return;
                    onPickRef.current({ plans, mapPoint: point });
                })
                .catch((e) => {
                    if (current.signal.aborted) return;
                    log.error('Feil ved oppslag av plan i kartet:', e);
                });
        });

        const cancelOutsideMap = (e: Event) => {
            if (mapView.container?.contains(e.target as Node)) return;
            controller?.abort();
        };
        const cancelOnEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') controller?.abort();
        };
        document.addEventListener('pointerdown', cancelOutsideMap, true);
        document.addEventListener('focusin', cancelOutsideMap);
        document.addEventListener('keydown', cancelOnEscape);

        return () => {
            handle.remove();
            controller?.abort();
            document.removeEventListener('pointerdown', cancelOutsideMap, true);
            document.removeEventListener('focusin', cancelOutsideMap);
            document.removeEventListener('keydown', cancelOnEscape);
            dispatch(setDisableMapClick(false));
        };
    }, [enabled, mapView, arealplanClient, dispatch]);

    // Måleverktøyet deler flagget og slår det av når det lukkes — sett det på
    // igjen, ellers kjører standard kart-klikk ved siden av plan-oppslaget.
    useEffect(() => {
        if (enabled && mapView && disableMapClick === false) dispatch(setDisableMapClick(true));
    }, [enabled, mapView, disableMapClick, dispatch]);
}
